import { DayPhases, PhysicsMechanics } from "./mechanics/physics_mechanics"
import { NightSky } from "./models"

/**
 * The moon over a camp's nights. Each camp counts its own nights from its first, which is always moonlit, and the
 * moon comes round every MOON_CYCLE_NIGHTS nights with one moonless night in each round. Rain clouds over the camp
 * hide whatever moon there is (moonShown); when they clear, the cycle's sky is back.
 *
 * `t` is the world clock's tick (server Clock.now(): seconds, a day being dayLengthClockCycles() of them).
 */

/** How many nights the moon takes to come round again. */
export const MOON_CYCLE_NIGHTS = 5
/** Which night of each round (0: the first) has no moon: the last. */
export const MOONLESS_NIGHT = 4

/**
 * Where in the day one night hands over to the next, as a share of the day: the middle of its full daylight (the
 * `day` phase, between the end of the morning and the end of the day), when no sky can be seen.
 */
export const NIGHT_TURNS_AT = DayPhases.morning && DayPhases.day
    ? (DayPhases.morning.dayPct + DayPhases.day.dayPct) / 2 : 0.375

const days = (t: number) => t / PhysicsMechanics.dayLengthClockCycles()

/** Which night the clock is in: night n runs from the middle of day n's daylight to the middle of day n + 1's. */
export const nightIndex = (t: number): number => Math.floor(days(t) - NIGHT_TURNS_AT)

/**
 * The night a camp opened at `t` counts as its first: the one under way, or the one coming when it opens by day.
 * Opened in the last hours of a night, the next one counts as its first and the rest of this one goes with it.
 */
export const moonEpoch = (t: number): number => Math.round(days(t) - NIGHT_TURNS_AT)

/** The night of a camp's round of nights at `t`, counting from 0 at its first (`epoch`, moonEpoch). */
export const campNight = (epoch: number, t: number): number => Math.max(0, nightIndex(t) - epoch)

/** The sky the moon cycle gives a camp whose first night is `epoch`, at `t`. */
export const cycleSky = (epoch: number, t: number): NightSky =>
    campNight(epoch, t) % MOON_CYCLE_NIGHTS === MOONLESS_NIGHT ? "moonless" : "moonlit"

/**
 * How much moon shows, 0..1: none on a moonless night; on a moonlit one, all of it less the rain clouds over the
 * camp (`cloud`, 0..1, as rainEnvelope gives it). A pinned sky (/ember night) ignores the clouds.
 */
export const moonShown = (sky: NightSky | undefined, pinned: boolean | undefined, cloud: number): number =>
    sky === "moonless" ? 0 : pinned ? 1 : 1 - Math.min(1, Math.max(0, cloud || 0))
